/**
 * ConditionTreeEditor — MTF-aware compact condition-tree editor for schema-v2.
 *
 * Fully controlled: receives `tree` and emits `onChange(newTree)`.
 *
 * MTF support:
 *  - TF and Offset are always visible INLINE per operand (no hidden ⚙ row)
 *  - deriveEvalTF() computes the smallest TF used across all conditions
 *  - Eval TF + MTF hint shown at section header level
 *
 * Operand state shape:
 *  { kind: 'series'|'constant', source: 'price'|'indicator', name, timeframe, offset, params?, output? }
 *  { kind: 'constant', value }
 */
import { useState, useMemo, useRef, useEffect } from 'react';
import { ChevronDown, Trash2, Plus, GitBranch, Pencil } from 'lucide-react';
import { cn } from '../../lib/utils';
import {
  COMPARATOR_OPTIONS,
  CROSS_COMPARATORS,
  INDICATOR_DEFAULTS,
  INDICATOR_NAMES,
  PRICE_NAMES,
  TIMEFRAME_OPTIONS,
  addConditionToNode,
  addGroupToNode,
  setGroupOperator,
  updateNodeInTree,
  removeNodeFromTree,
} from './schemaV2';

// ─── MTF utilities ──────────────────────────────────────────────────────────────

const TF_RANK = { '1m': 1, '3m': 2, '5m': 3, '10m': 4, '15m': 5, '30m': 6, '1h': 7, '1d': 8 };

function collectTreeTFs(node, result = new Set()) {
  if (!node) return result;
  if (node.node_type === 'condition') {
    if (node.left?.kind  === 'series' && node.left.timeframe)  result.add(node.left.timeframe);
    if (node.right?.kind === 'series' && node.right.timeframe) result.add(node.right.timeframe);
  }
  if (node.children) node.children.forEach((c) => collectTreeTFs(c, result));
  return result;
}

/** Returns the smallest timeframe used across all conditions in the tree. */
export function deriveEvalTF(tree, fallback = '5m') {
  const tfs = collectTreeTFs(tree);
  if (tfs.size === 0) return fallback;
  return [...tfs].reduce((min, tf) =>
    (TF_RANK[tf] || 99) < (TF_RANK[min] || 99) ? tf : min
  );
}

// ─── Candle field display labels ────────────────────────────────────────────────

const CANDLE_FIELD_LABELS = {
  open:                 'Open',
  high:                 'High',
  low:                  'Low',
  close:                'Close',
  ltp:                  'LTP',
  volume:               'Volume',
  range:                'Range',
  body:                 'Body',
  upper_wick:           'Upper Wick',
  lower_wick:           'Lower Wick',
  body_pct_range:       'Body % of Range',
  upper_wick_pct_range: 'UW % of Range',
  lower_wick_pct_range: 'LW % of Range',
};

// ─── Operand label ─────────────────────────────────────────────────────────────
// Offset excluded — it's always shown as an inline select beside the operand.

function operandLabel(op) {
  if (!op) return '—';
  if (op.kind === 'constant') return `${op.value ?? '?'}`;
  if (op.kind === 'series') {
    if (op.source === 'price') return CANDLE_FIELD_LABELS[op.name] || op.name || 'close';
    if (op.source === 'indicator') {
      const params = op.params ? Object.values(op.params).join(',') : '';
      const paramsStr = params ? `(${params})` : '';
      const output = op.output ? `.${op.output}` : '';
      return `${op.name}${paramsStr}${output}`;
    }
  }
  return '?';
}

function operandTitle(op, fallbackTF) {
  if (!op) return '';
  if (op.kind === 'constant') return `Constant: ${op.value ?? '?'}`;
  if (op.kind === 'series') {
    const tf       = op.timeframe || fallbackTF || '?';
    const off      = parseInt(op.offset ?? 0, 10);
    const offLabel = off === 0 ? 'latest' : `${off} candle${off !== 1 ? 's' : ''} back`;
    return `${operandLabel(op)} (${tf}, ${offLabel})`;
  }
  return '';
}

// Human-readable labels for comparator dropdown (overrides schemaV2 symbols)
const COMPARATOR_DISPLAY = {
  GT:          '> Greater than',
  GTE:         '≥ Greater or equal',
  LT:          '< Less than',
  LTE:         '≤ Less or equal',
  EQ:          '= Equal',
  CROSS_ABOVE: 'Crosses ↑',
  CROSS_BELOW: 'Crosses ↓',
};

// ─── Compact atoms ─────────────────────────────────────────────────────────────

function PickerSelect({ value, options, onChange }) {
  return (
    <div className="relative">
      <select
        className="w-full bg-bg-card border border-bg-border rounded px-2 py-1 text-2xs font-mono text-txt-primary appearance-none pr-5 cursor-pointer"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      <ChevronDown size={10} className="absolute right-1.5 top-1/2 -translate-y-1/2 text-txt-muted pointer-events-none" />
    </div>
  );
}

function PickerInput({ onChange, ...props }) {
  return (
    <input
      className="w-full bg-bg-card border border-bg-border rounded px-2 py-1 text-2xs font-mono text-txt-primary"
      {...props}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

// Used by row display chips
const OFFSET_OPTIONS = [
  { value: '0', label: '(Latest)' },
  { value: '1', label: '(-1)'     },
  { value: '2', label: '(-2)'     },
  { value: '3', label: '(-3)'     },
  { value: '4', label: '(-4)'     },
  { value: '5', label: '(-5)'     },
];

// Used by the popover Offset dropdown
const OFFSET_SELECT_OPTIONS = [
  { value: '0',      label: 'Latest'              },
  { value: '1',      label: '1 candle back (-1)'  },
  { value: '2',      label: '2 candles back (-2)' },
  { value: '3',      label: '3 candles back (-3)' },
  { value: '4',      label: '4 candles back (-4)' },
  { value: '5',      label: '5 candles back (-5)' },
  { value: 'custom', label: 'Custom…'             },
];

// ─── OperandPicker ─────────────────────────────────────────────────────────────
// TF and Offset are NOT in this panel — they live inline in ConditionRow.

function OperandPicker({ value, onChange, timeframe, catalogData }) {
  const indicators = catalogData?.indicators?.length
    ? catalogData.indicators.map((i) => i.name)
    : INDICATOR_NAMES;

  const kind      = value?.kind   || 'series';
  const source    = value?.source || 'price';
  const activeTab = kind === 'constant' ? 'constant' : source === 'indicator' ? 'indicator' : 'price';

  // Track explicit "Custom…" selection for the offset dropdown
  const savedOffset    = parseInt(value?.offset ?? 0, 10);
  const [customOffset, setCustomOffset] = useState(savedOffset > 5);

  function switchToPrice() {
    onChange({ kind: 'series', source: 'price', name: 'close', timeframe: value?.timeframe || timeframe, offset: 0 });
  }
  function switchToIndicator() {
    const name = indicators[0] || 'EMA';
    const defaults = INDICATOR_DEFAULTS[name] || { params: {} };
    onChange({ kind: 'series', source: 'indicator', name, timeframe: value?.timeframe || timeframe, offset: 0, params: { ...defaults.params } });
  }
  function switchToConstant() {
    onChange({ kind: 'constant', value: 0 });
  }

  return (
    <div className="space-y-1.5">
      {/* Type tabs */}
      <div className="flex gap-1">
        {[
          { id: 'price',     label: 'Candle'    },
          { id: 'indicator', label: 'Indicator' },
          { id: 'constant',  label: 'Value'     },
        ].map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => {
              if (tab.id === 'price')          switchToPrice();
              else if (tab.id === 'indicator') switchToIndicator();
              else                             switchToConstant();
            }}
            className={cn(
              'px-2 py-0.5 rounded text-2xs font-mono border transition-colors',
              activeTab === tab.id
                ? 'border-amber/40 bg-amber/10 text-amber'
                : 'border-bg-border text-txt-secondary hover:border-bg-hover hover:text-txt-primary'
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Price: inline label + compact dropdown */}
      {activeTab === 'price' && (
        <div className="flex items-center gap-2">
          <span className="text-2xs font-mono text-txt-muted shrink-0">Field</span>
          <div className="max-w-[140px] flex-1 min-w-0">
            <PickerSelect
              value={value?.name || 'close'}
              options={PRICE_NAMES.map((p) => ({ value: p, label: CANDLE_FIELD_LABELS[p] || p }))}
              onChange={(v) => onChange({ ...value, kind: 'series', source: 'price', name: v })}
            />
          </div>
        </div>
      )}

      {/* Indicator: full-width name; compact params; inline output */}
      {activeTab === 'indicator' && (
        <div className="space-y-1.5">
          <div>
            <div className="text-2xs font-mono text-txt-muted mb-0.5">Indicator</div>
            <div className="max-w-[160px]">
              <PickerSelect
                value={value?.name || 'EMA'}
                options={indicators.map((n) => ({ value: n, label: n }))}
                onChange={(n) => {
                  const defaults = INDICATOR_DEFAULTS[n] || { params: {} };
                  onChange({
                    kind: 'series', source: 'indicator', name: n,
                    timeframe: value?.timeframe || timeframe,
                    offset: value?.offset || 0,
                    params: { ...defaults.params },
                    output: undefined,
                  });
                }}
              />
            </div>
          </div>

          {Object.keys(value?.params || {}).length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(value.params).map(([k, v]) => (
                <div key={k} className="flex flex-col">
                  <span className="text-2xs font-mono text-txt-muted mb-0.5">{k}</span>
                  <div className="w-14">
                    <PickerInput
                      type="number" min="1"
                      value={v ?? ''}
                      onChange={(val) => onChange({ ...value, params: { ...value.params, [k]: Number(val) || v } })}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}

          {INDICATOR_DEFAULTS[value?.name]?.outputs && (
            <div className="flex items-center gap-2">
              <span className="text-2xs font-mono text-txt-muted shrink-0">Output</span>
              <div className="max-w-[120px] flex-1 min-w-0">
                <PickerSelect
                  value={value?.output || INDICATOR_DEFAULTS[value.name].outputs[0]}
                  options={INDICATOR_DEFAULTS[value.name].outputs.map((o) => ({ value: o, label: o }))}
                  onChange={(v) => onChange({ ...value, output: v })}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Constant: just the input, no duplicate label */}
      {activeTab === 'constant' && (
        <div className="w-28">
          <PickerInput
            type="number" step="0.01" placeholder="e.g. 70"
            value={value?.value ?? ''}
            onChange={(v) => onChange({ kind: 'constant', value: v === '' ? null : Number(v) })}
          />
        </div>
      )}

      {/* LTP note — no candle context */}
      {activeTab === 'price' && value?.source === 'price' && value?.name === 'ltp' && (
        <p className="text-2xs font-mono text-txt-muted/60 border-t border-bg-border/40 pt-1.5">
          LTP uses live market price
        </p>
      )}

      {/* TF + Offset: single row — hidden for LTP (live price, no candle context) */}
      {activeTab !== 'constant' && !(activeTab === 'price' && value?.source === 'price' && value?.name === 'ltp') && (
        <div className="border-t border-bg-border/40 pt-1.5">
          <div className="flex gap-2 items-end">
            {/* TF */}
            <div>
              <div className="text-2xs font-mono text-txt-muted mb-0.5">TF</div>
              <div className="w-[68px]">
                <PickerSelect
                  value={value?.timeframe || timeframe}
                  options={TIMEFRAME_OPTIONS.map((t) => ({ value: t, label: t }))}
                  onChange={(v) => onChange({ ...value, timeframe: v })}
                />
              </div>
            </div>
            {/* Offset */}
            <div className="flex-1 min-w-0">
              <div className="text-2xs font-mono text-txt-muted mb-0.5">Offset</div>
              <div className="flex items-center gap-1">
                <PickerSelect
                  value={customOffset ? 'custom' : String(savedOffset)}
                  options={OFFSET_SELECT_OPTIONS}
                  onChange={(v) => {
                    if (v === 'custom') {
                      setCustomOffset(true);
                    } else {
                      setCustomOffset(false);
                      onChange({ ...value, offset: parseInt(v, 10) });
                    }
                  }}
                />
                {customOffset && (
                  <div className="w-14 shrink-0">
                    <PickerInput
                      type="number"
                      min="0"
                      value={String(savedOffset)}
                      onChange={(v) => onChange({ ...value, offset: Math.max(0, parseInt(v, 10) || 0) })}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── ConditionRow ──────────────────────────────────────────────────────────────
// Layout: [left chip] [TF] [Offset]   [cmp]   [right chip] [TF] [Offset]   [✏] [🗑]
// TF and Offset shown as read-only display chips; click to open popover.

function ConditionRow({ node, tree, onTreeChange, expandedPicker, setExpandedPicker, timeframe, catalogData, rowIndex = 0 }) {
  const rowRef     = useRef(null);
  const lastSideRef = useRef('left');

  const isCross        = CROSS_COMPARATORS.has(node.comparator);
  const leftOpen       = expandedPicker?.nodeId === node._id && expandedPicker?.side === 'left';
  const rightOpen      = expandedPicker?.nodeId === node._id && expandedPicker?.side === 'right';
  const anyOpen        = leftOpen || rightOpen;
  const hasLeftSeries  = node.left?.kind  === 'series';
  const hasRightSeries = node.right?.kind === 'series';

  function togglePicker(side) {
    lastSideRef.current = side;
    setExpandedPicker(
      (leftOpen && side === 'left') || (rightOpen && side === 'right')
        ? null
        : { nodeId: node._id, side }
    );
  }

  function handleEditClick() {
    if (anyOpen) {
      setExpandedPicker(null);
    } else {
      setExpandedPicker({ nodeId: node._id, side: lastSideRef.current });
    }
  }

  // Click-outside + Escape to close popover
  useEffect(() => {
    if (!anyOpen) return;
    function onDown(e) {
      if (rowRef.current && !rowRef.current.contains(e.target)) setExpandedPicker(null);
    }
    function onKey(e) { if (e.key === 'Escape') setExpandedPicker(null); }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [anyOpen, setExpandedPicker]);

  const leftOffset    = node.left?.offset  ?? 0;
  const rightOffset   = node.right?.offset ?? 0;
  const leftOffLabel  = OFFSET_OPTIONS.find((o) => o.value === String(leftOffset))?.label  ?? String(leftOffset);
  const rightOffLabel = OFFSET_OPTIONS.find((o) => o.value === String(rightOffset))?.label ?? String(rightOffset);

  // Shared classes for display chips (TF / Offset)
  const displayChip = (active) => cn(
    'shrink-0 inline-flex items-center rounded px-1 py-0.5 text-2xs font-mono border transition-colors cursor-pointer',
    active
      ? 'bg-amber/10 text-amber border-amber/40'
      : 'bg-bg-surface text-txt-muted border-bg-border/40 hover:border-amber/30 hover:text-txt-secondary'
  );

  const isLeftLTP  = node.left?.source  === 'price' && node.left?.name  === 'ltp';
  const isRightLTP = node.right?.source === 'price' && node.right?.name === 'ltp';

  return (
    <div ref={rowRef} className={cn(
      'relative rounded border border-bg-border border-l-2 border-l-amber/20 p-1.5 transition-colors duration-150',
      'hover:border-l-amber/35 hover:bg-bg-elevated/40',
      rowIndex % 2 === 0 ? 'bg-bg-elevated/20' : 'bg-bg-elevated/10'
    )}>

      {/* ── Inline condition row ── */}
      <div
        className="flex items-center gap-1 flex-nowrap overflow-x-auto min-w-0 [&::-webkit-scrollbar]:hidden"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >

        {/* Left: TF chip (series, not LTP) — shown before field name */}
        {hasLeftSeries && !isLeftLTP && (
          <span onClick={() => togglePicker('left')} title={operandTitle(node.left, timeframe)} className={displayChip(leftOpen)}>
            {node.left.timeframe || timeframe}
          </span>
        )}

        {/* Left: operand chip */}
        <button
          type="button"
          onClick={() => togglePicker('left')}
          title={operandTitle(node.left, timeframe)}
          className={cn(
            'shrink-0 inline-flex items-center rounded px-1.5 py-0.5 text-2xs font-mono font-medium border transition-colors cursor-pointer',
            leftOpen
              ? 'bg-amber/10 text-amber border-amber/40'
              : 'bg-bg-elevated text-txt-primary border-bg-border/60 hover:border-amber/40 hover:text-amber'
          )}
        >
          {operandLabel(node.left)}
        </button>

        {/* Left: Offset chip (series, not LTP) */}
        {hasLeftSeries && !isLeftLTP && (
          <span onClick={() => togglePicker('left')} title={operandTitle(node.left, timeframe)} className={displayChip(leftOpen)}>
            {leftOffLabel}
          </span>
        )}

        {/* Comparator */}
        <div className="shrink-0 relative">
          <select
            className={cn(
              'bg-bg-surface border rounded px-1.5 py-1 text-2xs font-mono appearance-none pr-5 cursor-pointer',
              node.comparator === 'CROSS_ABOVE'
                ? 'border-bull/30 bg-bull/5 text-bull font-semibold'
                : node.comparator === 'CROSS_BELOW'
                ? 'border-bear/30 bg-bear/5 text-bear font-semibold'
                : 'border-bg-border/40 text-txt-muted'
            )}
            value={node.comparator}
            onChange={(e) => onTreeChange(updateNodeInTree(tree, node._id, { comparator: e.target.value }))}
          >
            {COMPARATOR_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {COMPARATOR_DISPLAY[opt.value] ?? opt.label}
              </option>
            ))}
          </select>
          <ChevronDown size={9} className="absolute right-0.5 top-1/2 -translate-y-1/2 text-txt-muted pointer-events-none" />
        </div>

        {/* Right: TF chip (series, not LTP) — shown before field name */}
        {hasRightSeries && !isRightLTP && (
          <span onClick={() => togglePicker('right')} title={operandTitle(node.right, timeframe)} className={displayChip(rightOpen)}>
            {node.right.timeframe || timeframe}
          </span>
        )}

        {/* Right: operand chip — slightly softer than left to establish hierarchy */}
        <button
          type="button"
          onClick={() => togglePicker('right')}
          title={operandTitle(node.right, timeframe)}
          className={cn(
            'shrink-0 inline-flex items-center rounded px-1.5 py-0.5 text-2xs font-mono font-medium border transition-colors cursor-pointer',
            rightOpen
              ? 'bg-amber/10 text-amber border-amber/40'
              : 'bg-bg-elevated text-txt-secondary border-bg-border/40 hover:border-amber/40 hover:text-amber'
          )}
        >
          {operandLabel(node.right)}
        </button>

        {/* Right: Offset chip (series, not LTP) */}
        {hasRightSeries && !isRightLTP && (
          <span onClick={() => togglePicker('right')} title={operandTitle(node.right, timeframe)} className={displayChip(rightOpen)}>
            {rightOffLabel}
          </span>
        )}

        {/* Edit toggle — opens last-interacted operand, or closes if open */}
        <button
          type="button"
          onClick={handleEditClick}
          title={anyOpen ? 'Close editor' : 'Edit operand'}
          className={cn(
            'shrink-0 p-1 rounded transition-colors ml-auto',
            anyOpen
              ? 'text-amber bg-amber/10'
              : 'text-txt-muted hover:text-amber hover:bg-amber/10'
          )}
        >
          <Pencil size={11} />
        </button>

        {/* Remove */}
        <button
          type="button"
          onClick={() => {
            setExpandedPicker(null);
            onTreeChange(removeNodeFromTree(tree, node._id));
          }}
          className="shrink-0 p-1 rounded text-txt-muted hover:text-bear hover:bg-bear/10 transition-colors"
          title="Remove condition"
        >
          <Trash2 size={12} />
        </button>
      </div>

      {/* Left operand popover */}
      {leftOpen && (
        <div className="absolute top-full left-0 mt-1 z-[60] w-56 rounded border border-amber/20 bg-bg-elevated p-2 space-y-2 shadow-panel animate-fade-in">
          <OperandPicker
            value={node.left}
            onChange={(op) => onTreeChange(updateNodeInTree(tree, node._id, { left: op }))}
            timeframe={timeframe}
            catalogData={catalogData}
          />
        </div>
      )}

      {/* Right operand popover */}
      {rightOpen && (
        <div className="absolute top-full right-0 mt-1 z-[60] w-56 rounded border border-amber/20 bg-bg-elevated p-2 space-y-2 shadow-panel animate-fade-in">
          <OperandPicker
            value={node.right}
            onChange={(op) => onTreeChange(updateNodeInTree(tree, node._id, { right: op }))}
            timeframe={timeframe}
            catalogData={catalogData}
          />
        </div>
      )}

      {/* CROSS warnings */}
      {isCross && hasLeftSeries && hasRightSeries && node.left.timeframe !== node.right.timeframe && (
        <p className="text-2xs font-mono text-warn/80 pl-1 mt-0.5">
          CROSS requires both operands to use the same timeframe.
        </p>
      )}
      {isCross && !(hasLeftSeries && hasRightSeries) && (
        <p className="text-2xs font-mono text-info/70 pl-1 mt-0.5">
          CROSS: both operands must be series.
        </p>
      )}
    </div>
  );
}

// ─── GroupNode ─────────────────────────────────────────────────────────────────

function GroupNode({ node, depth, tree, onTreeChange, expandedPicker, setExpandedPicker, timeframe, catalogData }) {
  const isEmpty = (node.children || []).length === 0;
  return (
    <div className="space-y-1">

      {/* Group header */}
      <div className="flex items-center gap-1.5">
        {/* Root: show "Match All"/"Match Any" only when ≥2 children; nested: always show AND/OR */}
        {(depth > 0 || (node.children || []).length >= 2) && (
          <div className="flex gap-0.5 shrink-0">
            {(depth === 0
              ? [{ op: 'AND', label: 'Match All' }, { op: 'OR', label: 'Match Any' }]
              : [{ op: 'AND', label: 'AND'       }, { op: 'OR', label: 'OR'        }]
            ).map(({ op, label }) => (
              <button
                key={op}
                type="button"
                onClick={() => onTreeChange(setGroupOperator(tree, node._id, op))}
                className={cn(
                  'px-2 py-0.5 rounded text-2xs font-mono border transition-colors',
                  node.operator === op
                    ? 'border-amber/40 bg-amber/10 text-amber'
                    : 'border-bg-border text-txt-muted hover:border-bg-hover hover:text-txt-primary'
                )}
              >
                {label}
              </button>
            ))}
          </div>
        )}

        {depth > 0 && (
          <span className="text-2xs font-mono text-txt-muted">
            {node.operator === 'OR' ? 'Any of' : 'All of'}
          </span>
        )}

        {depth > 0 && (
          <button
            type="button"
            onClick={() => { setExpandedPicker(null); onTreeChange(removeNodeFromTree(tree, node._id)); }}
            className="p-1 rounded text-txt-muted hover:text-bear hover:bg-bear/10 transition-colors"
            title="Remove group"
          >
            <Trash2 size={11} />
          </button>
        )}
      </div>

      {/* Children */}
      <div className={cn(
        'space-y-1',
        depth > 0 && 'ml-1 pl-2 border-l-2 border-bg-border/60 rounded-r bg-bg-surface/20 py-1.5 pr-1'
      )}>
        {isEmpty && (
          <p className="text-2xs font-mono text-txt-muted/40 italic px-1 py-0.5">
            Add a condition to define this group.
          </p>
        )}
        {(node.children || []).map((child, idx) =>
          child.node_type === 'group'
            ? (
              <GroupNode
                key={child._id}
                node={child}
                depth={depth + 1}
                tree={tree}
                onTreeChange={onTreeChange}
                expandedPicker={expandedPicker}
                setExpandedPicker={setExpandedPicker}
                timeframe={timeframe}
                catalogData={catalogData}
                rowIndex={idx}
              />
            )
            : (
              <ConditionRow
                key={child._id}
                node={child}
                tree={tree}
                onTreeChange={onTreeChange}
                expandedPicker={expandedPicker}
                setExpandedPicker={setExpandedPicker}
                timeframe={timeframe}
                catalogData={catalogData}
                rowIndex={idx}
              />
            )
        )}
      </div>

      {/* Add actions */}
      <div className="flex gap-1">
        <button
          type="button"
          onClick={() => onTreeChange(addConditionToNode(tree, node._id, timeframe))}
          className="flex items-center gap-1 px-2 py-0.5 rounded text-2xs font-mono border border-bg-border text-txt-muted hover:border-amber/30 hover:text-amber transition-colors"
        >
          <Plus size={10} /> Condition
        </button>
        {!isEmpty && (
          <button
            type="button"
            onClick={() => onTreeChange(addGroupToNode(tree, node._id))}
            className="flex items-center gap-1 px-2 py-0.5 rounded text-2xs font-mono border border-bg-border text-txt-muted hover:border-info/30 hover:text-info transition-colors"
          >
            <GitBranch size={10} /> Group
          </button>
        )}
      </div>
    </div>
  );
}

// ─── ConditionTreeEditor (main export) ────────────────────────────────────────

/**
 * @param {object}   props.tree         - current tree (group node with _id)
 * @param {function} props.onChange      - (newTree) => void
 * @param {string}   props.timeframe     - default TF seeded into new conditions
 * @param {object}   [props.catalogData] - from useStrategyConditionCatalog()
 */
export default function ConditionTreeEditor({ tree, onChange, timeframe = '5m', catalogData }) {
  const [expandedPicker, setExpandedPicker] = useState(null);

  const isEmpty = !tree?.children || tree.children.length === 0;

  // Derive evaluation TF from all conditions in this section
  const { evalTF, isMTF } = useMemo(() => {
    if (!tree || isEmpty) return { evalTF: timeframe, isMTF: false };
    const allTFs = collectTreeTFs(tree);
    const ef     = deriveEvalTF(tree, timeframe);
    return { evalTF: ef, isMTF: allTFs.size > 1 };
  }, [tree, timeframe, isEmpty]);

  if (!tree) {
    return <p className="text-2xs font-mono text-txt-muted">No tree initialized.</p>;
  }

  return (
    <div className="space-y-1.5">

      {/* Eval TF bar — shown only when conditions exist */}
      {!isEmpty && (
        <div className="flex items-center gap-1.5 text-2xs font-mono">
          <span className="text-txt-muted">Eval:</span>
          <span className="text-amber font-semibold">{evalTF}</span>
          {isMTF && (
            <span className="text-info/70 ml-0.5">· MTF</span>
          )}
        </div>
      )}

      {/* MTF guidance — only when multiple TFs are in use */}
      {isMTF && (
        <p className="text-2xs font-mono text-txt-muted/80 leading-relaxed">
          Evaluates on <span className="text-amber">{evalTF}</span>.
          Higher TF values use the latest completed candle.
        </p>
      )}

      <GroupNode
        node={tree}
        depth={0}
        tree={tree}
        onTreeChange={(newTree) => onChange(newTree)}
        expandedPicker={expandedPicker}
        setExpandedPicker={setExpandedPicker}
        timeframe={timeframe}
        catalogData={catalogData}
      />

      {isEmpty && (
        <p className="text-2xs font-mono text-bear pl-1">
          Add at least one condition, or disable this section.
        </p>
      )}
    </div>
  );
}
